import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAccount } from "../api/account";
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";
import { Separator } from "@t-one-internal/design-system";
import logo from "../assets/logo.png";
import performsparkLogo from "../assets/Performspark.png";
import traineryLearnLogo from "../assets/TraineryLEARN.jpg";
import compBldrLogo from "../assets/CompBldr.svg";
import { Checkbox } from "@t-one-internal/design-system/lib/components/ui/checkbox";

// Regex pattern for org name: only alphabets, numbers, spaces, and periods
const ORG_NAME_PATTERN = /^[a-zA-Z0-9\s.]*$/;

export default function RegisterForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    account_name: "",
    admin_first_name: "",
    admin_last_name: "",
    admin_email: "",
    create_demo_data: true,
    selected_modules: ["perform-spark"] as string[],
  });
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [orgNameError, setOrgNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Special validation for organization name
    if (name === 'account_name') {
      if (value && !ORG_NAME_PATTERN.test(value)) {
        setOrgNameError('Only letters, numbers, spaces, and periods are allowed');
        return; // Don't update the value if it contains invalid characters
      }
      setOrgNameError('');
    }

    setFormData({ ...formData, [name]: value });
  };

  const toggleModule = (moduleId: string, checked: boolean | "indeterminate") => {
    setFormData((prev) => ({
      ...prev,
      selected_modules:
        checked === true
          ? [...prev.selected_modules, moduleId]
          : prev.selected_modules.filter((m) => m !== moduleId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setErrorCode("");
    setIsSubmitting(true);

    try {
      const response = await createAccount(formData);
      console.log("Account created successfully", formData, response);
      navigate("/verify-email", {
        state: {
          email: formData.admin_email,
          first_name: formData.admin_first_name,
          user_uuid: String(response.user.user_uuid),
          account_uuid: String(response.account.account_uuid),
        },
      });
    } catch (error: any) {
      console.error("Error creating account:", error);

      // Extract error message and code from various possible response formats
      let errorMessage = "Failed to create account. Please try again.";
      let code = "";

      if (error.response?.data?.code) {
        code = error.response.data.code;
      }

      if (error.response?.data?.error) {
        const errorText = error.response.data.error;

        // Try to extract the nested errorMessage if it exists
        const nestedMessageMatch = errorText.match(/\{"errorMessage":"([^"]+)"\}/);
        if (nestedMessageMatch && nestedMessageMatch[1]) {
          errorMessage = nestedMessageMatch[1];
        } else {
          errorMessage = errorText;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setErrorCode(code);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex bg-white overflow-hidden">
      {/* Left Side - Register Form */}
      <div className="w-full lg:w-[45%] lg:max-w-[45vw] flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-24 py-4 sm:py-6 relative z-10">
        <div className="mb-4 sm:mb-5">
          <div className="flex items-center mb-3 sm:mb-4">
            <img src={logo} alt="Trainery Logo" className="h-8 sm:h-10" />
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Create Your Organization</h1>
          <p className="text-gray-500 text-sm sm:text-base leading-snug sm:leading-relaxed">
            Register your organization to get started with Trainery
          </p>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-4 rounded-lg text-sm">
            <p className="mb-3">{error}</p>
            {/* {errorCode === "REGISTRATION_FAILED" && (
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full py-2.5 px-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-lg font-medium transition-colors duration-200"
              >
                Go to Login
              </button>
            )} */}
          </div>
        )}

        <div className="space-y-3 sm:space-y-4">
          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
            <div className="space-y-1 sm:space-y-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Organization Name</span>
              <Input
                id="account_name"
                name="account_name"
                type="text"
                placeholder="Enter organization name"
                value={formData.account_name}
                onChange={handleChange}
                className={`h-10 sm:h-11 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200 ${orgNameError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}`}
                required
                disabled={isSubmitting}
              />
              {orgNameError && (
                <p className="text-xs text-red-500 mt-1">{orgNameError}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
              <div className="space-y-1 sm:space-y-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Admin First Name</span>
                <Input
                  id="admin_first_name"
                  name="admin_first_name"
                  type="text"
                  placeholder="Enter first name"
                  value={formData.admin_first_name}
                  onChange={handleChange}
                  className="h-10 sm:h-11 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-1 sm:space-y-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Admin Last Name</span>
                <Input
                  id="admin_last_name"
                  name="admin_last_name"
                  type="text"
                  placeholder="Enter last name"
                  value={formData.admin_last_name}
                  onChange={handleChange}
                  className="h-10 sm:h-11 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="space-y-1 sm:space-y-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Admin Email</span>
              <Input
                id="admin_email"
                name="admin_email"
                type="email"
                placeholder="Enter admin email"
                value={formData.admin_email}
                onChange={handleChange}
                className="h-10 sm:h-11 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Module Selection */}
            <div className="space-y-1.5 sm:space-y-2 pt-1 sm:pt-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Select Modules for 14-Day Trial</span>
              <div className="space-y-2">
                <label className="flex items-center gap-3 sm:gap-4 py-2 px-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <Checkbox
                    checked={formData.selected_modules.includes("perform-spark")}
                    onCheckedChange={(checked) => toggleModule("perform-spark", checked)}
                    disabled={isSubmitting}
                  />
                  <div className="w-24 sm:w-28 flex-shrink-0 flex items-center justify-center">
                    <img src={performsparkLogo} alt="PerformSpark" className="h-4 sm:h-5 w-auto" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">PerformSpark</span>
                    <p className="text-xs text-gray-500 hidden sm:block">Goals, Reviews & Continuous Feedback</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 sm:gap-4 py-2 px-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <Checkbox
                    checked={formData.selected_modules.includes("learn")}
                    onCheckedChange={(checked) => toggleModule("learn", checked)}
                    disabled={isSubmitting}
                  />
                  <div className="w-24 sm:w-28 flex-shrink-0 flex items-center justify-center">
                    <img src={traineryLearnLogo} alt="TraineryLearn" className="h-5 sm:h-6 w-auto" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">TraineryLearn</span>
                    <p className="text-xs text-gray-500 hidden sm:block">LMS, Training Management & Coaching</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 sm:gap-4 py-2 px-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <Checkbox
                    checked={formData.selected_modules.includes("comp-builder")}
                    onCheckedChange={(checked) => toggleModule("comp-builder", checked)}
                    disabled={isSubmitting}
                  />
                  <div className="w-24 sm:w-28 flex-shrink-0 flex items-center justify-center">
                    <img src={compBldrLogo} alt="CompBldr" className="h-4 sm:h-5 w-auto" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">CompBldr</span>
                    <p className="text-xs text-gray-500 hidden sm:block">Compensation Design & Pay Equity Analytics</p>
                  </div>
                </label>
              </div>
              {formData.selected_modules.length === 0 && (
                <p className="text-xs text-red-500">Please select at least one module</p>
              )}
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="create_demo_data"
                checked={formData.create_demo_data}
                onCheckedChange={(checked: boolean | "indeterminate") => setFormData({ ...formData, create_demo_data: checked === true })}
                disabled={isSubmitting}
              />
              <label htmlFor="create_demo_data" className="text-sm text-gray-700 cursor-pointer">
                Include demo data
              </label>
            </div>

            <Button
              type="submit"
              className="w-full h-10 sm:h-11 text-sm sm:text-base font-semibold bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-lg shadow-lg shadow-indigo-200 transition-all duration-200 mt-1 sm:mt-2"
              disabled={isSubmitting || formData.selected_modules.length === 0}
            >
              {isSubmitting ? "Creating Organization..." : "Create Organization"}
            </Button>
          </form>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full border-gray-100" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-gray-400 font-medium tracking-wider">Or</span>
            </div>
          </div>

          <div className="text-center text-xs sm:text-sm text-gray-600">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/login")}
              className="font-medium text-[#4F46E5] hover:underline"
            >
              Sign in here
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden items-center justify-center p-6 xl:p-12 flex-col bg-gray-50 border-l border-gray-100">
        {/* Decorative gradient orbs */}
        <div className="absolute top-[-200px] right-[-100px] w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 60%)' }} />
        <div className="absolute bottom-[-150px] left-[-80px] w-[400px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 60%)' }} />

        <div className="relative z-10 w-full max-w-lg flex flex-col gap-4 xl:gap-8">
          {/* Tagline */}
          <div>
            <div className="w-10 h-0.5 bg-[#4F46E5] mb-4 xl:mb-6 rounded-full" />
            <p className="text-sm xl:text-lg text-indigo-500/80 font-medium tracking-wide mb-1 xl:mb-2">The complete</p>
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
              Talent Management
            </h2>
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
              Platform
            </h2>
            <p className="text-sm xl:text-base text-gray-500 mt-2 xl:mt-4 leading-relaxed">
              Three powerful modules. One unified platform.
            </p>
          </div>

          {/* Product module cards */}
          <div className="flex flex-col gap-3 xl:gap-4">
            {/* PerformSpark */}
            <div className="group flex items-center gap-4 xl:gap-5 bg-white rounded-2xl px-4 xl:px-6 py-4 xl:py-5 border border-gray-100 border-l-4 border-l-[#4F46E5] shadow-sm hover:shadow-md transition-all duration-300">
              <div className="w-24 xl:w-28 flex-shrink-0 flex items-center justify-center">
                <img src={performsparkLogo} alt="PerformSpark" className="h-5 xl:h-6 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base xl:text-lg font-bold text-gray-900">PerformSpark</h3>
                  <a href="https://www.performspark.ai/" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#4F46E5]/60 tracking-wide group-hover:text-[#4F46E5] transition-colors">Explore &rarr;</a>
                </div>
                <p className="text-xs xl:text-sm text-gray-500 mt-1 xl:mt-1.5 leading-snug xl:leading-relaxed">
                  Drive performance with real-time goals, reviews & continuous feedback across your organization.
                </p>
              </div>
            </div>

            {/* TraineryLearn */}
            <div className="group flex items-center gap-4 xl:gap-5 bg-white rounded-2xl px-4 xl:px-6 py-4 xl:py-5 border border-gray-100 border-l-4 border-l-[#4F46E5] shadow-sm hover:shadow-md transition-all duration-300">
              <div className="w-24 xl:w-28 flex-shrink-0 flex items-center justify-center">
                <img src={traineryLearnLogo} alt="TraineryLearn" className="h-5 xl:h-6 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base xl:text-lg font-bold text-gray-900">TraineryLearn</h3>
                  <span className="text-xs font-medium text-[#4F46E5]/60 tracking-wide group-hover:text-[#4F46E5] transition-colors">Explore &rarr;</span>
                </div>
                <p className="text-xs xl:text-sm text-gray-500 mt-1 xl:mt-1.5 leading-snug xl:leading-relaxed">
                  Deliver compliant training programs & track certifications at scale for regulated industries.
                </p>
              </div>
            </div>

            {/* CompBldr */}
            <div className="group flex items-center gap-4 xl:gap-5 bg-white rounded-2xl px-4 xl:px-6 py-4 xl:py-5 border border-gray-100 border-l-4 border-l-[#4F46E5] shadow-sm hover:shadow-md transition-all duration-300">
              <div className="w-24 xl:w-28 flex-shrink-0 flex items-center justify-center">
                <img src={compBldrLogo} alt="CompBldr" className="h-5 xl:h-6 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base xl:text-lg font-bold text-gray-900">CompBldr</h3>
                  <a href="https://compbldr.ai/" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#4F46E5]/60 tracking-wide group-hover:text-[#4F46E5] transition-colors">Explore &rarr;</a>
                </div>
                <p className="text-xs xl:text-sm text-gray-500 mt-1 xl:mt-1.5 leading-snug xl:leading-relaxed">
                  Design competitive compensation structures with data-driven insights & pay equity analytics.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom lockup */}
        <div className="absolute bottom-8 flex items-center gap-2.5">
          <img src={logo} alt="Trainery" className="h-6 opacity-30" />
          <div className="w-px h-4 bg-gray-300" />
          <span className="text-[10px] font-medium tracking-widest uppercase text-gray-400">
            Talent Management Solution
          </span>
        </div>
      </div>
    </div>
  );
}
